import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { BillboardMedia } from '@/features/billboards/api/billboardApi';
import { isVideoMedia } from '@/features/billboards/utils/billboardDetailUtils';

type MediaGalleryProps = {
  media: BillboardMedia[];
  activeIndex: number;
  screenName?: string;
  onSelect: (index: number) => void;
};

export function MediaGallery({
  media,
  activeIndex,
  screenName,
  onSelect,
}: MediaGalleryProps) {
  const activeMedia = media[activeIndex];
  const hasMultiple = media.length > 1;

  /* Modified by: Ishan | Soft-contrast media gallery with gradient fallback and styled slider controls */
  return (
    <div className="min-w-0">
      <div className="relative h-[280px] sm:h-[340px] w-full overflow-hidden rounded-[20px] bg-gradient-to-br from-[#2B3E8A] via-[#8156B4] to-[#1E9B6E]">
        {activeMedia?.url ? (
          isVideoMedia(activeMedia.url, activeMedia.mediaType) ? (
            <video
              key={activeMedia.url}
              src={activeMedia.url}
              controls
              muted
              loop
              playsInline
              className="h-full w-full object-contain"
            />
          ) : (
            <img
              src={activeMedia.url}
              alt={screenName || 'Screen'}
              className="h-full w-full object-cover"
            />
          )
        ) : (
          <div className="flex h-full items-center justify-center text-sm font-medium text-white/70">
            No media available
          </div>
        )}

        {hasMultiple ? (
          <>
            <button
              type="button"
              aria-label="Previous"
              disabled={activeIndex === 0}
              onClick={() => onSelect(activeIndex > 0 ? activeIndex - 1 : activeIndex)}
              className="absolute top-1/2 left-3.5 flex h-[38px] w-[38px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[rgba(30,27,58,0.55)] text-white shadow hover:bg-[rgba(30,27,58,0.8)] transition disabled:cursor-not-allowed disabled:opacity-25"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              type="button"
              aria-label="Next"
              disabled={activeIndex === media.length - 1}
              onClick={() =>
                onSelect(
                  activeIndex < media.length - 1 ? activeIndex + 1 : activeIndex,
                )
              }
              className="absolute top-1/2 right-3.5 flex h-[38px] w-[38px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-[rgba(30,27,58,0.55)] text-white shadow hover:bg-[rgba(30,27,58,0.8)] transition disabled:cursor-not-allowed disabled:opacity-25"
            >
              <ChevronRight size={18} />
            </button>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <div className="mt-3 flex gap-2.5 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {media.map((item, index) => (
            <button
              key={item.id || index}
              type="button"
              onClick={() => onSelect(index)}
              className={`h-[64px] w-[90px] shrink-0 cursor-pointer overflow-hidden rounded-[14px] transition ${
                activeIndex === index
                  ? 'outline outline-2 outline-[#8156B4] outline-offset-2'
                  : 'border border-[#EEEBF8] opacity-75 hover:opacity-100'
              }`}
            >
              {isVideoMedia(item.url, item.mediaType) ? (
                <video
                  src={item.url}
                  className="h-full w-full object-cover"
                  muted
                  playsInline
                />
              ) : (
                <img
                  src={item.url}
                  alt=""
                  className="h-full w-full object-cover"
                />
              )}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
